import { expandChartBounds } from './expandChartBounds';
import {
  mergeLiveCandleIntoCurrent,
  normalizeLiveCandleToChartBucket,
} from './mergeLiveCandle';

export function candlesAreEqual(left, right) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return left == null && right == null;
  }

  return (
    left.timeStamp === right.timeStamp &&
    left.liveSourceTimeStamp === right.liveSourceTimeStamp &&
    left.open === right.open &&
    left.high === right.high &&
    left.low === right.low &&
    left.close === right.close &&
    left.volume === right.volume &&
    left.slotIndex === right.slotIndex
  );
}

export function buildLiveCandleUpdate({
  liveCandle,
  currentCandle,
  millisBetweenCandles,
  numberOfCandles,
  timeUnit,
  candleDuration,
  sessionWindows = [],
  priceMinimum,
  priceMaximum,
}) {
  if (!liveCandle || !millisBetweenCandles) {
    return null;
  }

  const normalizedLiveCandle = normalizeLiveCandleToChartBucket({
    liveCandle,
    timeUnit,
    candleDuration,
    sessionWindows,
  });
  const liveTimeStamp = Number(normalizedLiveCandle?.timeStamp);
  if (!Number.isFinite(liveTimeStamp)) {
    return null;
  }

  if (!currentCandle) {
    return {
      type: 'initialize',
      nextCandle: normalizedLiveCandle,
      nextBounds: expandChartBounds({
        candle: normalizedLiveCandle,
        priceMinimum,
        priceMaximum,
      }),
      nextCandleTimeStamp: liveTimeStamp + millisBetweenCandles,
    };
  }

  const currentBucket = Number(currentCandle.timeStamp);
  if (!Number.isFinite(currentBucket)) {
    return {
      type: 'initialize',
      nextCandle: normalizedLiveCandle,
      nextBounds: expandChartBounds({
        candle: normalizedLiveCandle,
        priceMinimum,
        priceMaximum,
      }),
      nextCandleTimeStamp: liveTimeStamp + millisBetweenCandles,
    };
  }

  if (liveTimeStamp > currentBucket) {
    const slotAdvance = Math.max(
      Math.round((liveTimeStamp - currentBucket) / millisBetweenCandles),
      1
    );
    const nextCandle = {
      ...normalizedLiveCandle,
      slotIndex:
        (currentCandle.slotIndex ?? numberOfCandles - 1) + slotAdvance,
    };

    return {
      type: 'advance',
      closedCandle: currentCandle,
      nextCandle,
      nextBounds: expandChartBounds({
        candle: nextCandle,
        priceMinimum,
        priceMaximum,
      }),
      nextCandleTimeStamp: liveTimeStamp + millisBetweenCandles,
    };
  }

  if (liveTimeStamp < currentBucket) {
    return null;
  }

  const nextCandle = mergeLiveCandleIntoCurrent({
    currentCandle,
    normalizedLiveCandle,
  });

  return {
    type: candlesAreEqual(currentCandle, nextCandle) ? 'noop' : 'merge',
    nextCandle,
    nextBounds: expandChartBounds({
      candle: nextCandle,
      priceMinimum,
      priceMaximum,
    }),
    nextCandleTimeStamp: liveTimeStamp + millisBetweenCandles,
  };
}
